import { FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { z } from 'zod';
import { NullElement } from '@/components/blocks/NullElement';
import { CatalogEntryIARecommendationSkeleton } from '@/components/content/CatalogEntryIARecommendation/parts/CatalogEntryIARecommendationSkeleton';
import { CatalogEntryCarousel } from '@/components/content/CatalogEntryRecommendation/parts/CatalogEntryCarousel';
import { fillPersonalization } from '@/components/blocks/DpRepurchaseCoupon/fillPersonalization';
import {
	usePersonalizationPurchase,
	usePersonalizationTokens,
} from '@/components/blocks/DpRepurchaseCoupon/usePersonalizationTokens';
import { RepurchasePricingProvider } from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchasePricingProvider';
import { RepurchaseSectionHeader } from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseSectionHeader';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { useCatalogEntryIARecommendationItems } from '@/data/Content/CatalogEntryIARecommendationItems';
import { DppIACarouselType, useDppIARecommendation } from '@/data/Content/DppIARecommendation';
import {
	usePersonalizationDppSizes,
	usePersonalizationSeed,
} from '@/data/Content/RepurchaseCoupon';
import { usePersonalizationFamilyPool } from '@/data/Content/RepurchaseFamilyPool';
import {
	productsWithoutSeedGender,
	withoutSeedGender,
} from '@/data/Content/RepurchaseFamilyPoolRules';
import { personalizationSizesFromDpp } from '@/data/dpp/sizes';
import { ContentProvider } from '@/data/context/content';
import { EventsContext } from '@/data/context/events';
import { useSettings } from '@/data/Settings';
import { ProductType } from '@/data/types/Product';
import { catalogEntryOrderIaRecommendation } from '@/utils/catalogEntryOrder';
import { useContractByUserContext } from '@/utils/contractByUserContext';
// eslint-disable-next-line no-restricted-imports
import { DpIARecommendationResponse } from 'integration/generated/transactions/data-contracts';

const CLICK_SOURCE_BY_TYPE: Record<DppIACarouselType, CLICK_PRODUCT_VIEW_SOURCE> = {
	outfit: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_OUTFIT,
	complements: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_COMPLEMENTS,
	recommendation: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_RECOMMENDED,
	sameBrand: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_SAME_BRAND,
	family: CLICK_PRODUCT_VIEW_SOURCE.PERSONALIZATION_FAMILY,
};

type FamilyPoolAttributes = {
	spothombre?: string;
	spotmujer?: string;
	spotnino?: string;
	poolsize?: string;
	familypriority?: string;
};

const useDppRecommendationRequest = (
	type: DppIACarouselType,
	{ spothombre, spotmujer, spotnino, poolsize, familypriority }: FamilyPoolAttributes
) => {
	const isFamily = type === 'family';
	const contract = useContractByUserContext();
	const { gender, loading: loadingGender } = usePersonalizationPurchase();
	const { partNumbers, loading } = usePersonalizationFamilyPool({
		spots:
			isFamily && !loadingGender
				? withoutSeedGender({ hombre: spothombre, mujer: spotmujer, nino: spotnino }, gender)
				: {},
		size: Number(poolsize) || undefined,
		priority: familypriority,
		contract,
	});
	const productsSelector = useCallback(
		(products: ProductType[]) => productsWithoutSeedGender(products, gender),
		[gender]
	);

	return isFamily
		? {
				seedPartNumbers: partNumbers,
				productsSelector,
				loading: loadingGender || loading,
				empty: !partNumbers.length,
		  }
		: { loading: false, empty: false };
};

const usePersonalizationRecomSizes = () => {
	const { dppSizes, loading } = usePersonalizationDppSizes();
	const sizes = useMemo(() => personalizationSizesFromDpp(dppSizes), [dppSizes]);
	return { sizes, loading };
};

const transformSchema = z.object({
	order: z.enum([
		'similarity',
		'ascending-price',
		'descending-price',
		'ascending-offer',
		'descending-offer',
	]),
});

const filtersSchema = z.array(
	z.object({
		name: z.string(),
		operation: z.union([z.string(), z.record(z.unknown())]),
	})
);

const MIN_PRODUCTS = 1;

const dppCarouselSX: SxProps<Theme> = {
	'& > :last-child': { marginBottom: { xs: '24px', sm: '40px', md: 0 } },
};

const useCarouselListView = ({
	products,
	isLoadingProducts,
	id,
	clickSource,
	parentPartNumber,
}: {
	products: ProductType[];
	isLoadingProducts: boolean;
	id: string;
	clickSource: CLICK_PRODUCT_VIEW_SOURCE;
	parentPartNumber: string;
}) => {
	const { onCarouselItemListView } = useContext(EventsContext);
	const { settings } = useSettings();
	const trackedKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (isLoadingProducts || !products.length || !settings) return;

		const item_list_id = clickSource ?? id;
		const key = `${item_list_id}|${products.map((p) => p.partNumber).join(',')}`;
		if (trackedKeyRef.current === key) return;
		trackedKeyRef.current = key;

		onCarouselItemListView({
			gtm: { parentPartNumber, products, item_list_id, settings },
		});
	}, [
		products,
		isLoadingProducts,
		parentPartNumber,
		clickSource,
		id,
		settings,
		onCarouselItemListView,
	]);
};

const DppCarousel: FC<{
	id: string;
	title?: string;
	type: DppIACarouselType;
	partNumber: string;
	transform: unknown;
	filters: unknown;
	header?: JSX.Element;
	seedPartNumbers?: string[];
	productsSelector?: (products: ProductType[]) => ProductType[];
}> = ({
	id,
	title,
	type,
	partNumber,
	transform,
	filters,
	header,
	seedPartNumbers,
	productsSelector,
}) => {
	const { sizes } = usePersonalizationRecomSizes();
	const order = useMemo(() => transformSchema.safeParse(transform).data?.order, [transform]);
	const ruleFilters = useMemo(() => filtersSchema.safeParse(filters).data ?? [], [filters]);
	const orderExtractor = useCallback(
		(products: ProductType[], data: DpIARecommendationResponse) => {
			const selected = productsSelector ? productsSelector(products) : products;
			return order ? catalogEntryOrderIaRecommendation(order, selected, data) : selected;
		},
		[order, productsSelector]
	);
	const params = useMemo(() => ({ partNumber }), [partNumber]);
	const clickSource = CLICK_SOURCE_BY_TYPE[type];
	const contract = useContractByUserContext();

	const { products, isLoadingRecommendation, isLoadingProducts } = useDppIARecommendation({
		partNumber,
		type,
		filters: ruleFilters,
		sizes,
		contract,
		orderExtractor,
		seedPartNumbers,
		withoutIdentity: true,
	});

	const { slides, onNotify } = useCatalogEntryIARecommendationItems({
		products,
		isLoadingProducts,
		parentPartNumber: partNumber,
		variant: 'largeRepurchase',
		clickSource,
		contract,
		params,
	});

	useCarouselListView({
		products,
		isLoadingProducts,
		id,
		clickSource,
		parentPartNumber: partNumber,
	});

	if (isLoadingRecommendation) {
		return <CatalogEntryIARecommendationSkeleton id={id} title={title} />;
	}

	const countable = products.filter((product) => product.partNumber !== partNumber);
	if (!isLoadingProducts && countable.length < MIN_PRODUCTS) {
		return <NullElement />;
	}

	return (
		<>
			{header}
			<RepurchasePricingProvider products={products}>
				<ContentProvider value={{ onNotify }}>
					<CatalogEntryCarousel slides={slides} id={id} />
				</ContentProvider>
			</RepurchasePricingProvider>
		</>
	);
};

export const DpCarouselDppIARecommendation: FC<
	{
		id: string;
		title?: string;
		type?: DppIACarouselType;
		transform: unknown;
		filters: unknown;
		eyebrow?: string;
		subtitle?: string;
		chip?: string;
		anchor?: string;
	} & FamilyPoolAttributes
> = ({
	id,
	title: rawTitle,
	type = 'recommendation',
	transform,
	filters,
	eyebrow,
	subtitle,
	chip,
	anchor,
	...familyPoolAttributes
}) => {
	const { partNumber, loading: loadingSeed } = usePersonalizationSeed();
	const { loading: loadingSizes } = usePersonalizationRecomSizes();
	const request = useDppRecommendationRequest(type, familyPoolAttributes);
	const tokens = usePersonalizationTokens();
	const title = fillPersonalization(rawTitle, tokens) || undefined;
	const header = title ? (
		<RepurchaseSectionHeader
			anchor={anchor}
			eyebrow={fillPersonalization(eyebrow, tokens)}
			title={title}
			subtitle={fillPersonalization(subtitle, tokens)}
			chip={fillPersonalization(chip, tokens)}
		/>
	) : undefined;

	if (loadingSeed || loadingSizes || request.loading) {
		return <CatalogEntryIARecommendationSkeleton id={id} title={title} />;
	}

	if (!partNumber || request.empty) {
		return null;
	}

	return (
		<Box sx={dppCarouselSX}>
			<DppCarousel
				id={id}
				title={title}
				type={type}
				partNumber={partNumber}
				transform={transform}
				filters={filters}
				header={header}
				seedPartNumbers={request.seedPartNumbers}
				productsSelector={request.productsSelector}
			/>
		</Box>
	);
};
